'use client';

import { useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';

interface NavigationItem {
  label: string;
  path: string;
  icon: string;
  description?: string;
}

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const pathname = usePathname();

  const navigationItems: NavigationItem[] = [
    {
      label: 'DIY Guides',
      path: '/diy',
      icon: 'BookOpenIcon',
      description: 'DIY maintenance guides'
    },
    {
      label: 'Find Mechanics',
      path: '/home-page',
      icon: 'MagnifyingGlassIcon',
      description: 'Search for trusted mechanics'
    },
    {
      label: 'Compare Prices',
      path: '/price-compare',
      icon: 'ChartBarIcon',
      description: 'Compare repair costs'
    },
    {
      label: 'Find Parts',
      path: '/oem-parts-finder',
      icon: 'WrenchScrewdriverIcon',
      description: 'Discover OEM parts'
    },
    {
      label: 'Car Care 101',
      path: '/education',
      icon: 'AcademicCapIcon',
      description: 'Learn car care basics'
    },
    {
      label: 'Service Milestones',
      path: '/milestones',
      icon: 'ClipboardDocumentCheckIcon',
      description: 'Maintenance checklists'
    }
  ];

  const utilityItems: NavigationItem[] = [
    {
      label: 'For Mechanics',
      path: '/claim-listing',
      icon: 'BuildingStorefrontIcon',
      description: 'Claim your listing'
    },
    /* Temporarily disabled until Vercel Neon Auth is setup
    {
      label: 'Login',
      path: '/login',
      icon: 'UserCircleIcon',
      description: 'Admin login'
    },
    {
      label: 'Dashboard',
      path: '/admin-dashboard',
      icon: 'ChartPieIcon',
      description: 'Admin access'
    }
    */
  ];

  const isActive = (path: string) => pathname === path;

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-[100] bg-card shadow-elevation-md">
        <nav className="mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center justify-between">
            {/* Logo */}
            <Link
              href="/home-page"
              className="flex items-center space-x-2 transition-smooth hover:opacity-80"
              onClick={closeMobileMenu}
            >
              <div className="flex h-10 w-10 items-center justify-center rounded-md bg-primary">
                <Icon name="WrenchScrewdriverIcon" size={24} className="text-primary-foreground" />
              </div>
              <div className="flex flex-col">
                <span className="font-heading text-lg font-semibold leading-tight text-text-primary">
                  AutoFixIQ
                </span>
                <span className="font-caption text-xs text-text-secondary">
                  Smart decisions for car repair
                </span>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex lg:items-center lg:space-x-1">
              {navigationItems.map((item) => (
                <Link
                  key={item.path}
                  href={item.path}
                  className={`group relative flex items-center space-x-2 rounded-md px-4 py-2 font-body text-sm font-medium transition-smooth hover:bg-muted ${isActive(item.path)
                    ? 'text-primary' : 'text-text-secondary hover:text-text-primary'
                    }`}
                >
                  <Icon name={item.icon as any} size={18} />
                  <span>{item.label}</span>
                  {isActive(item.path) && (
                    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />
                  )}
                </Link>
              ))}
            </div>

            {/* Desktop Utility Navigation */}
            <div className="hidden lg:flex lg:items-center lg:space-x-2">
              {utilityItems.map((item) => (
                <Link
                  key={item.path}
                  href={item.path}
                  className={`flex items-center space-x-2 rounded-md px-4 py-2 font-body text-sm font-medium transition-smooth ${isActive(item.path)
                    ? 'bg-primary text-primary-foreground'
                    : 'border border-border text-text-secondary hover:border-primary hover:text-primary'
                    }`}
                >
                  <Icon name={item.icon as any} size={18} />
                  <span>{item.label}</span>
                </Link>
              ))}
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={toggleMobileMenu}
              className="lg:hidden flex items-center justify-center rounded-md p-2 text-text-secondary transition-smooth hover:bg-muted hover:text-text-primary focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
              aria-label="Toggle menu"
              aria-expanded={isMobileMenuOpen}
            >
              <Icon
                name={isMobileMenuOpen ? 'XMarkIcon' : 'Bars3Icon'}
                size={24}
              />
            </button>
          </div>

          {/* Mobile Navigation Menu */}
          {isMobileMenuOpen && (
            <div className="lg:hidden fixed inset-0 top-16 z-[200] bg-card">
              <div className="flex h-full flex-col overflow-y-auto px-4 py-6">
                {/* Primary Navigation */}
                <div className="space-y-2">
                  <p className="font-caption text-xs font-medium uppercase tracking-wider text-muted-foreground px-4 py-2">
                    Main Menu
                  </p>
                  {navigationItems.map((item) => (
                    <Link
                      key={item.path}
                      href={item.path}
                      onClick={closeMobileMenu}
                      className={`flex items-center space-x-3 rounded-lg px-4 py-3 transition-smooth ${isActive(item.path)
                        ? 'bg-primary text-primary-foreground'
                        : 'text-text-secondary hover:bg-muted hover:text-text-primary'
                        }`}
                    >
                      <Icon name={item.icon as any} size={24} />
                      <div className="flex flex-col">
                        <span className="font-body text-base font-medium">
                          {item.label}
                        </span>
                        {item.description && (
                          <span className="font-caption text-xs opacity-80">
                            {item.description}
                          </span>
                        )}
                      </div>
                    </Link>
                  ))}
                </div>

                {/* Utility Navigation */}
                <div className="mt-8 space-y-2 border-t border-border pt-6">
                  <p className="font-caption text-xs font-medium uppercase tracking-wider text-muted-foreground px-4 py-2">
                    Quick Access
                  </p>
                  {utilityItems.map((item) => (
                    <Link
                      key={item.path}
                      href={item.path}
                      onClick={closeMobileMenu}
                      className={`flex items-center space-x-3 rounded-lg px-4 py-3 transition-smooth ${isActive(item.path)
                        ? 'bg-primary text-primary-foreground'
                        : 'text-text-secondary hover:bg-muted hover:text-text-primary'
                        }`}
                    >
                      <Icon name={item.icon as any} size={24} />
                      <div className="flex flex-col">
                        <span className="font-body text-base font-medium">
                          {item.label}
                        </span>
                        {item.description && (
                          <span className="font-caption text-xs opacity-80">
                            {item.description}
                          </span>
                        )}
                      </div>
                    </Link>
                  ))}
                </div>

                {/* Mobile Contact Actions */}
                <div className="mt-auto pt-6 border-t border-border">
                  <button className="w-full flex items-center justify-center space-x-2 rounded-lg bg-accent px-6 py-4 font-body text-base font-medium text-accent-foreground transition-smooth hover:opacity-90 active:scale-[0.97]">
                    <Icon name="PhoneIcon" size={20} />
                    <span>Call Support</span>
                  </button>
                </div>
              </div>
            </div>
          )}
        </nav>
      </header>
      {/* Universal Spacer: Perfectly handles viewport clearance below the fixed header */}
      <div className="h-16" />
    </>
  );
};

export default Header;